/** Main application shell: municipality selection, filters, and working map. */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AboutPanel } from "./components/AboutPanel";
import { ExportModal } from "./components/ExportModal";
import { FloatingSelectionSummary } from "./components/FloatingSelectionSummary";
import { LayerControls } from "./components/LayerControls";
import { Map as WatercourseMap } from "./components/Map";
import { MapModeSwitch } from "./components/MapModeSwitch";
import { MeasureCatalogueModal } from "./components/MeasureCatalogueModal";
import { MeasureDetails } from "./components/MeasureDetails";
import { MunicipalitySelect } from "./components/MunicipalitySelect";
import { OnboardingTour, type TourStep } from "./components/OnboardingTour";
import { Sidebar } from "./components/Sidebar";
import { WarningBanner } from "./components/WarningBanner";
import { apiClient } from "./api/client";
import { useCatalogue } from "./hooks/useCatalogue";
import { useConfig } from "./hooks/useConfig";
import { useGemeindeLocations } from "./hooks/useGemeindeLocations";
import { LanguageProvider, useLanguageContext } from "./hooks/useLanguage";
import { useMapData } from "./hooks/useMapData";
import { lineIntersectsPolygon, closeRing, boundsToPolygonRing } from "./utils/geometry";
import { parseMeasureIds } from "./utils/measures";
import { clipNoWorkLines } from "./utils/noWork";
import type {
  FilterState,
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  LayerVisibility,
  MapMode,
  RegionConfig,
} from "./types";

const MEASURE_COLORS_STORAGE_KEY = "flima_measure_colors";
const APP_TOUR_START_STEP_INDEX = 2;
const NO_WORK_SPECIES_ID = "helm_azurjungfer";

// Legacy on-map area-selection tools (polygon draw + viewport capture, feeding
// the FloatingSelectionSummary/PDF path). Hidden in favour of the export modal,
// but kept wired up so flipping this back to `true` restores them intact.
const SHOW_LEGACY_SELECTION_TOOLS = false;

/** Minimal shape of the live MapLibre instance we read to seed the export
 *  preview's camera. Stored on the map container by the Map component. */
type LiveMapInstance = {
  getCenter: () => { lng: number; lat: number };
  getZoom: () => number;
};

function getStoredMeasureColors(): Record<string, string> {
  try {
    const raw = localStorage.getItem(MEASURE_COLORS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function getInitialMunicipality() {
  return new URLSearchParams(window.location.search).get("gemeinde");
}

function getInitialMapMode(): MapMode {
  return new URLSearchParams(window.location.search).get("mode") === "planning"
    ? "planning"
    : "habitats";
}

function AppContent({ config }: { config: RegionConfig }) {
  const { t, lang, toggleLang } = useLanguageContext();
  const [selectedMunicipality, setSelectedMunicipality] = useState<string | null>(
    getInitialMunicipality,
  );
  const { catalogue } = useCatalogue(true);
  const [selectedMeasure, setSelectedMeasure] = useState<GeoJSONFeature | null>(null);
  const [highlightedMeasureIds, setHighlightedMeasureIds] = useState<Set<string>>(new Set());
  const [highlightMeasureId, setHighlightMeasureId] = useState<string | null>(null);
  const [lineOverlayMeasureId, setLineOverlayMeasureId] = useState<string | null>(null);
  const [highlightNoWork, setHighlightNoWork] = useState(false);
  const [lineOverlayNoWork, setLineOverlayNoWork] = useState(false);
  type SelectionMode = "none" | "polygon" | "viewport";
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("none");
  const [polygonPoints, setPolygonPoints] = useState<Array<[number, number]>>([]);
  const [polygonClosed, setPolygonClosed] = useState(false);
  const [viewportPolygon, setViewportPolygon] = useState<number[][] | null>(null);
  const [mapZoom, setMapZoom] = useState(10);
  const mapExportRef = useRef<HTMLDivElement>(null);
  const municipalityBoundaryCacheRef = useRef<Map<string, GeoJSONFeatureCollection>>(new Map());
  const municipalityPreviewRequestRef = useRef(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportView, setExportView] = useState<{ center: [number, number]; zoom: number } | null>(
    null,
  );
  const [previewMunicipalityData, setPreviewMunicipalityData] =
    useState<GeoJSONFeatureCollection | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mapMode, setMapMode] = useState<MapMode>(getInitialMapMode);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(() =>
    getInitialMunicipality() ? APP_TOUR_START_STEP_INDEX : 0,
  );
  const [filters, setFilters] = useState<FilterState>(() => ({
    species: new Set(config.species.map((species) => species.id)),
    months: new Set([new Date().getMonth() + 1]),
    measures: new Set(config.measures.map((measure) => measure.id)),
  }));
  const [layerVisibility, setLayerVisibility] = useState<LayerVisibility>(() =>
    Object.fromEntries(
      ["species", "measures", ...Object.keys(config.base_layers)].map((layer) => [
        layer,
        // Land-area overlays start hidden to keep the initial map uncluttered.
        layer !== "nsg_areas" && layer !== "ffh_areas",
      ]),
    ),
  );
  const [customMeasureColors, setCustomMeasureColors] = useState<Record<string, string>>(
    getStoredMeasureColors,
  );
  const [basemap, setBasemap] = useState<"street" | "satellite">("street");
  const { municipalityLocations } = useGemeindeLocations();

  useEffect(() => {
    localStorage.setItem(MEASURE_COLORS_STORAGE_KEY, JSON.stringify(customMeasureColors));
  }, [customMeasureColors]);

  const handleMeasureColorChange = useCallback((measureId: string, color: string) => {
    setCustomMeasureColors((previous) => ({ ...previous, [measureId]: color }));
  }, []);

  const handleResetMeasureColors = useCallback(() => {
    setCustomMeasureColors({});
  }, []);

  // Config with any user-picked measure colours applied, so every consumer
  // (map layers, legend, details panel, selection summary) stays in sync
  // from this one override point.
  const effectiveConfig = useMemo<RegionConfig>(() => {
    if (Object.keys(customMeasureColors).length === 0) return config;
    return {
      ...config,
      measures: config.measures.map((measure) =>
        customMeasureColors[measure.id]
          ? { ...measure, color: customMeasureColors[measure.id] }
          : measure,
      ),
    };
  }, [config, customMeasureColors]);

  const {
    municipalityData,
    speciesData,
    measuresData,
    layersData,
    loading: mapLoading,
    error: mapError,
  } = useMapData(selectedMunicipality, config);

  const measureMonthsById = useMemo(
    () => new Map(config.measures.map((measure) => [measure.id, measure.months])),
    [config.measures],
  );
  const measureSpeciesById = useMemo(
    () => new Map(config.measures.map((measure) => [measure.id, measure.species || ""])),
    [config.measures],
  );
  const measureColorById = useMemo(
    () => new Map(effectiveConfig.measures.map((measure) => [measure.id, measure.color])),
    [effectiveConfig.measures],
  );

  const tourSteps = useMemo<TourStep[]>(
    () => [
      {
        id: "intro",
        targetId: "landing-tour-card",
        title: t("tour_intro_title"),
        body: t("tour_intro_body"),
      },
      {
        id: "focus-area",
        targetId: "municipality-search",
        title: t("tour_focus_title"),
        body: t("tour_focus_body"),
        action: t("tour_focus_action"),
        isComplete: Boolean(selectedMunicipality),
        placement: "top",
      },
      {
        id: "mode-toggles",
        targetId: "mode-toggle-group",
        title: t("tour_modes_title"),
        body: t("tour_modes_body"),
      },
      {
        id: "habitat-map",
        targetId: "map-region",
        title: t("tour_map_title"),
        body: t("tour_map_body"),
      },
      {
        id: "species-controls",
        targetId: "sidebar-species",
        title: t("tour_species_title"),
        body: t("tour_species_body"),
      },
      {
        id: "measure-catalogue",
        targetId: "catalogue-button",
        title: t("tour_catalogue_title"),
        body: t("tour_catalogue_body"),
      },
      {
        id: "planning-mode",
        targetId: "planning-mode-button",
        title: t("tour_planning_title"),
        body: t("tour_planning_body"),
        action: t("tour_planning_action"),
        isComplete: mapMode === "planning",
      },
      {
        id: "planning-map",
        targetId: "map-region",
        title: t("tour_planning_map_title"),
        body: t("tour_planning_map_body"),
      },
      {
        id: "planning-filters",
        targetId: "sidebar-planning-filters",
        title: t("tour_filters_title"),
        body: t("tour_filters_body"),
      },
      {
        id: "measure-lines",
        targetId: "map-region",
        title: t("tour_measure_lines_title"),
        body: t("tour_measure_lines_body"),
        placement: "top",
      },
      {
        id: "map-tools",
        targetId: "map-tools",
        title: t("tour_tools_title"),
        body: t("tour_tools_body"),
      },
      {
        id: "layers",
        targetId: "layers-control",
        title: t("tour_layers_title"),
        body: t("tour_layers_body"),
      },
      {
        id: "export",
        targetId: "export-button",
        title: t("tour_export_title"),
        body: t("tour_export_body"),
      },
    ],
    [mapMode, selectedMunicipality, t],
  );

  // `municipality` is a stable UID (from the search dropdown or a map marker),
  // persisted to the URL and used as the fetch/identity key throughout.
  const handleMunicipalitySelect = useCallback((municipality: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("gemeinde", municipality);
    window.history.replaceState({}, "", url);
    setSelectedMunicipality(municipality);
    setSelectedMeasure(null);
  }, []);

  useEffect(() => {
    municipalityPreviewRequestRef.current += 1;
    setPreviewMunicipalityData(null);
  }, [selectedMunicipality]);

  const handleMunicipalityClear = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("gemeinde");
    url.searchParams.delete("mode");
    window.history.replaceState({}, "", url);
    setSelectedMunicipality(null);
    setSelectedMeasure(null);
    setMapMode("habitats");
  }, []);

  const handleLayerToggle = useCallback((layer: string) => {
    setLayerVisibility((previous) => ({ ...previous, [layer]: !previous[layer] }));
  }, []);

  const handleMeasureClose = useCallback(() => {
    setSelectedMeasure(null);
    setHighlightedMeasureIds(new Set());
    setHighlightMeasureId(null);
    setHighlightNoWork(false);
    setLineOverlayNoWork(false);
  }, []);

  const handleMeasureClick = useCallback((feature: GeoJSONFeature) => {
    setSelectedMeasure(feature);
    // Clicked features come back from MapLibre with array props stringified.
    setHighlightedMeasureIds(new Set(parseMeasureIds(feature.properties.measures)));
  }, []);

  const handleZoomChange = useCallback((zoom: number) => {
    setMapZoom(zoom);
  }, []);

  // Open the export modal, seeding its preview map with the live map's current
  // camera so it opens on the exact view the user was looking at.
  const handleOpenExport = useCallback(() => {
    const el = mapExportRef.current?.querySelector(".maplibregl-map") as
      | (HTMLElement & { __maplibreMap?: LiveMapInstance })
      | null;
    const map = el?.__maplibreMap;
    if (map) {
      const center = map.getCenter();
      setExportView({ center: [center.lng, center.lat], zoom: map.getZoom() });
    } else {
      setExportView(null);
    }
    setExportOpen(true);
  }, []);

  const handleSelectionClear = useCallback(() => {
    setSelectionMode("none");
    setPolygonPoints([]);
    setPolygonClosed(false);
    setViewportPolygon(null);
  }, []);

  const handleMapModeChange = useCallback((mode: MapMode) => {
    const url = new URL(window.location.href);
    if (mode === "planning") url.searchParams.set("mode", mode);
    else url.searchParams.delete("mode");
    window.history.replaceState({}, "", url);
    setMapMode(mode);
    setSelectedMeasure(null);
    handleSelectionClear();
  }, [handleSelectionClear]);

  const otherMunicipalityLocations = useMemo<GeoJSONFeatureCollection>(() => {
    if (!selectedMunicipality) return municipalityLocations;
    return {
      ...municipalityLocations,
      features: municipalityLocations.features.filter(
        (feature) => feature.properties.uid !== selectedMunicipality,
      ),
    };
  }, [municipalityLocations, selectedMunicipality]);

  // `selectedMunicipality` is a stable UID; resolve it to a display name via the
  // marker list (loaded once at mount), falling back to the loaded boundary's
  // name, then the raw value (so legacy `?gemeinde=<name>` links still read
  // sensibly before normalization kicks in).
  const selectedMunicipalityName = useMemo(() => {
    if (!selectedMunicipality) return "";
    const marker = municipalityLocations.features.find(
      (feature) => feature.properties.uid === selectedMunicipality,
    );
    if (marker) return String(marker.properties.name ?? "");
    const boundaryName = municipalityData?.features[0]?.properties?.Gmndn;
    if (typeof boundaryName === "string" && boundaryName) return boundaryName;
    return selectedMunicipality;
  }, [municipalityData, municipalityLocations, selectedMunicipality]);

  // Legacy links use `?gemeinde=<name>`. Once the markers load, rewrite a
  // name-valued selection to its UID (and update the URL) so marker-hiding and
  // identity comparisons all key off the UID.
  useEffect(() => {
    if (!selectedMunicipality) return;
    const features = municipalityLocations.features;
    if (features.length === 0) return;
    const alreadyUid = features.some((f) => f.properties.uid === selectedMunicipality);
    if (alreadyUid) return;
    const byName = features.find((f) => f.properties.name === selectedMunicipality);
    if (!byName?.properties.uid) return;
    const uid = String(byName.properties.uid);
    const url = new URL(window.location.href);
    url.searchParams.set("gemeinde", uid);
    window.history.replaceState({}, "", url);
    setSelectedMunicipality(uid);
  }, [municipalityLocations, selectedMunicipality]);

  const handleMunicipalityPreview = useCallback(
    async (municipality: string | null) => {
      const requestId = municipalityPreviewRequestRef.current + 1;
      municipalityPreviewRequestRef.current = requestId;
      if (!municipality || municipality === selectedMunicipality) {
        setPreviewMunicipalityData(null);
        return;
      }

      const cached = municipalityBoundaryCacheRef.current.get(municipality);
      if (cached) {
        setPreviewMunicipalityData(cached);
        return;
      }

      try {
        const data = await apiClient.getGemeinde(municipality);
        municipalityBoundaryCacheRef.current.set(municipality, data);
        if (municipalityPreviewRequestRef.current === requestId) {
          setPreviewMunicipalityData(data);
        }
      } catch (err) {
        if (municipalityPreviewRequestRef.current === requestId) {
          console.error("Municipality preview failed:", err);
          setPreviewMunicipalityData(null);
        }
      }
    },
    [selectedMunicipality],
  );

  const handleMapMunicipalitySelect = useCallback(
    (municipality: string) => {
      municipalityPreviewRequestRef.current += 1;
      setPreviewMunicipalityData(null);
      handleMunicipalitySelect(municipality);
    },
    [handleMunicipalitySelect],
  );

  const filteredSpeciesData = useMemo(() => {
    if (!speciesData) return null;
    // Synthetic "both species" overlap lines are never in the species filter
    // Set — show them only while every configured species is selected.
    const allSpeciesSelected = config.species.every((s) => filters.species.has(s.id));
    return {
      ...speciesData,
      features: speciesData.features.filter((feature) => {
        const speciesId = feature.properties.species_id;
        if (speciesId === "both") return allSpeciesSelected;
        return typeof speciesId === "string" && filters.species.has(speciesId);
      }),
    };
  }, [config.species, filters.species, speciesData]);

  // True if a measure's allowed months intersect the currently selected months.
  const measureMonthAllowed = useCallback(
    (measure: string): boolean => {
      const activeMonths = measureMonthsById.get(measure);
      return activeMonths?.some((month) => filters.months.has(month)) ?? false;
    },
    [measureMonthsById, filters.months],
  );

  const measureFilterAllowed = useCallback(
    (measure: string): boolean => {
      const owningSpecies = measureSpeciesById.get(measure);
      if (owningSpecies === undefined) return false;
      return (!owningSpecies || filters.species.has(owningSpecies)) && measureMonthAllowed(measure);
    },
    [filters.species, measureMonthAllowed, measureSpeciesById],
  );

  const filteredMeasuresData = useMemo(() => {
    if (!measuresData) return null;
    const allSpeciesSelected = config.species.every((s) => filters.species.has(s.id));
    return {
      ...measuresData,
      features: measuresData.features
        .filter((feature) => {
          const speciesId = feature.properties.species_id;
          // Synthetic "both species" overlap lines have measures: [] and are
          // never in the species filter Set — keep them only while every
          // configured species is selected, and skip the month check.
          if (speciesId === "both") return allSpeciesSelected;
          const activeMeasures = Array.isArray(feature.properties.measures)
            ? (feature.properties.measures as string[])
            : [];
          // Species-owned measures follow their species toggle; standalone
          // measures ignore species toggles. Both still follow the month filter.
          return activeMeasures.some(measureFilterAllowed);
        })
        .map((feature) => {
          // Strip each feature's `measures` down to the month-matching subset so
          // the map's per-measure slot layers (which read this array directly via
          // MapLibre expressions) only draw lines that also appear in the legend.
          // Without this, a line kept because ONE measure matches the month would
          // still draw slots for its other, out-of-season measures.
          const activeMeasures = Array.isArray(feature.properties.measures)
            ? (feature.properties.measures as string[])
            : [];
          const matched = activeMeasures.filter(measureFilterAllowed);
          const matchedStandaloneOnly =
            matched.length > 0 && matched.every((id) => !measureSpeciesById.get(id));
          if (!matchedStandaloneOnly && matched.length === activeMeasures.length) return feature;

          const properties: GeoJSONFeature["properties"] = {
            ...feature.properties,
            measures: matched,
          };
          if (matchedStandaloneOnly) {
            const color = measureColorById.get(matched[0]) || "#808080";
            properties.species_id = "";
            properties.species_name = t("standalone_measure");
            properties.species_color = color;
            properties.color = color;
            properties.overlap_type = "none";
          }
          return { ...feature, properties };
        }),
    };
  }, [
    config.species,
    measureColorById,
    measureFilterAllowed,
    measureSpeciesById,
    measuresData,
    t,
  ]);

  // Helm-Azurjungfer habitat lines where NO measure is allowed in the selected
  // months (line has no measures at all, or all fall outside the chosen months).
  // Bachmuschel-only habitat with no measure is intentionally left unmarked.
  //
  // These candidates are then spatially clipped against the workable lines:
  // different habitat lines can share a watercourse (e.g. a Bachmuschel
  // potential line running along a Helm line that has measures), so any stretch
  // already covered by a workable line is removed — the dashes end up mutually
  // exclusive with the coloured lines, showing only genuinely no-work habitat.
  const noWorkData = useMemo(() => {
    if (!measuresData) return null;
    const candidates = measuresData.features.filter((feature) => {
      const speciesId = feature.properties.species_id;
      if (speciesId !== NO_WORK_SPECIES_ID || !filters.species.has(speciesId)) return false;
      const activeMeasures = Array.isArray(feature.properties.measures)
        ? (feature.properties.measures as string[])
        : [];
      return !activeMeasures.some(measureFilterAllowed);
    });
    // Exclude synthetic overlap and standalone-measure rows from the species
    // workable set: neither should suppress genuine red-dashed "no work"
    // stretches for selected species habitat at the same location.
    const workable: GeoJSONFeatureCollection = filteredMeasuresData
      ? {
          ...filteredMeasuresData,
          features: filteredMeasuresData.features.filter(
            (feature) =>
              feature.properties.species_id !== "both" && Boolean(feature.properties.species_id),
          ),
        }
      : { type: "FeatureCollection", features: [] };
    return clipNoWorkLines({ type: "FeatureCollection", features: candidates }, workable);
  }, [
    filters.species,
    measureFilterAllowed,
    measuresData,
    filteredMeasuresData,
  ]);

  // Active selection ring (polygon or viewport)
  const activeSelectionRing = useMemo(() => {
    if (selectionMode === "polygon" && polygonClosed && polygonPoints.length >= 3) {
      return closeRing(polygonPoints);
    }
    if (selectionMode === "viewport" && viewportPolygon) {
      return viewportPolygon;
    }
    return null;
  }, [selectionMode, polygonClosed, polygonPoints, viewportPolygon]);

  // Features within the selection polygon
  const selectedAreaFeatures = useMemo(() => {
    if (!filteredMeasuresData || !activeSelectionRing) return [];
    return filteredMeasuresData.features.filter((feature) =>
      lineIntersectsPolygon(feature.geometry, activeSelectionRing),
    );
  }, [filteredMeasuresData, activeSelectionRing]);

  // Set of measure IDs that actually have features in the current municipality data.
  // Prevents "phantom" legend entries for measures with no features on the map.
  const presentMeasureIds = useMemo(() => {
    const ids = new Set<string>();
    if (measuresData) {
      for (const feature of measuresData.features) {
        const active = Array.isArray(feature.properties.measures)
          ? (feature.properties.measures as string[])
          : [];
        for (const m of active) ids.add(m);
      }
    }
    return ids;
  }, [measuresData]);

  const handleLegendMeasureClick = useCallback(
    (measureId: string) => {
      // Toggle: if already highlighted, unhighlight; otherwise highlight
      setHighlightedMeasureIds((previous) => {
        const next = new Set(previous);
        if (next.has(measureId)) {
          next.delete(measureId);
          setHighlightMeasureId(null);
        } else {
          next.clear();
          next.add(measureId);
          setHighlightMeasureId(measureId);
          setHighlightNoWork(false);
        }
        return next;
      });
    },
    [],
  );

  const handleLineOverlayToggle = useCallback((measureId: string) => {
    setLineOverlayMeasureId((previous) => (previous === measureId ? null : measureId));
    setLineOverlayNoWork(false);
  }, []);

  const handleNoWorkHighlightToggle = useCallback(() => {
    setHighlightNoWork((prev) => {
      const next = !prev;
      if (next) {
        setHighlightedMeasureIds(new Set());
        setHighlightMeasureId(null);
      }
      return next;
    });
  }, []);

  const handleNoWorkOverlayToggle = useCallback(() => {
    setLineOverlayNoWork((prev) => {
      const next = !prev;
      if (next) setLineOverlayMeasureId(null);
      return next;
    });
  }, []);

  const handlePolygonPointAdd = useCallback((lng: number, lat: number) => {
    setPolygonPoints((prev) => [...prev, [lng, lat] as [number, number]]);
    setPolygonClosed(false);
  }, []);

  const handlePolygonFinish = useCallback(() => {
    if (polygonPoints.length >= 3) {
      setPolygonClosed(true);
      setSelectionMode("polygon");
    }
  }, [polygonPoints]);

  const handlePolygonUndo = useCallback(() => {
    setPolygonPoints((prev) => prev.slice(0, -1));
    setPolygonClosed(false);
  }, []);

  const handleViewportCapture = useCallback((bounds: { west: number; south: number; east: number; north: number }) => {
    const ring = boundsToPolygonRing(bounds);
    setViewportPolygon(ring);
    setSelectionMode("viewport");
    setPolygonPoints([]);
    setPolygonClosed(false);
  }, []);

  const handlePolygonToggle = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev === "polygon") {
        setPolygonPoints([]);
        setPolygonClosed(false);
        return "none";
      }
      setViewportPolygon(null);
      setPolygonPoints([]);
      setPolygonClosed(false);
      return "polygon";
    });
  }, []);

  const handleExportPdf = useCallback(async () => {
    if (!mapExportRef.current) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(mapExportRef.current, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        scale: 2,
      });

      const imgData = canvas.toDataURL("image/png");
      const orientation = canvas.width >= canvas.height ? "landscape" : "portrait";
      const pdf = new jsPDF({
        orientation,
        unit: "px",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);

      const date = new Date().toISOString().slice(0, 10);
      const safeName = (selectedMunicipalityName || "selection").replace(/[^a-zA-Z0-9]/g, "_");
      pdf.save(`flima-selection-summary-${safeName}-${date}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
    }
  }, [selectedMunicipalityName]);

  const handleTourStart = useCallback(() => {
    setTourStepIndex(selectedMunicipality ? APP_TOUR_START_STEP_INDEX : 0);
    setTourOpen(true);
  }, [selectedMunicipality]);

  const handleTourDismiss = useCallback(() => {
    setTourOpen(false);
  }, []);

  const handleTourComplete = useCallback(() => {
    setTourOpen(false);
  }, []);

  const handleTourStepChange = useCallback(
    (nextIndex: number) => {
      const minIndex = selectedMunicipality ? APP_TOUR_START_STEP_INDEX : 0;
      setTourStepIndex(Math.max(minIndex, Math.min(nextIndex, tourSteps.length - 1)));
    },
    [selectedMunicipality, tourSteps.length],
  );

  useEffect(() => {
    if (!tourOpen) return;
    const stepId = tourSteps[tourStepIndex]?.id;
    if (!selectedMunicipality && tourStepIndex >= APP_TOUR_START_STEP_INDEX) {
      setTourStepIndex(0);
      return;
    }
    if (selectedMunicipality && stepId === "focus-area") {
      setTourStepIndex(APP_TOUR_START_STEP_INDEX);
      return;
    }

    if (stepId === "species-controls" || stepId === "planning-filters") {
      setSidebarOpen(true);
    } else if (
      stepId === "habitat-map" ||
      stepId === "mode-toggles" ||
      stepId === "measure-catalogue" ||
      stepId === "planning-mode" ||
      stepId === "planning-map" ||
      stepId === "measure-lines" ||
      stepId === "map-tools" ||
      stepId === "layers" ||
      stepId === "export"
    ) {
      setSidebarOpen(false);
    }
  }, [selectedMunicipality, tourOpen, tourStepIndex, tourSteps]);

  if (!selectedMunicipality) {
    return (
      <>
        <MunicipalitySelect
          config={config}
          onSelect={handleMunicipalitySelect}
          onTourStart={handleTourStart}
        />
        <OnboardingTour
          open={tourOpen}
          steps={tourSteps}
          stepIndex={tourStepIndex}
          onStepChange={handleTourStepChange}
          onClose={handleTourDismiss}
          onComplete={handleTourComplete}
        />
      </>
    );
  }

  return (
    <div className="flima-screen flex flex-col overflow-hidden bg-flima-mist">
      <a
        href="#map-region"
        className="sr-only z-50 rounded-md bg-white px-4 py-2 text-flima-spruce focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus-visible:ring-2 focus-visible:ring-flima-river"
      >
        {t("skip_to_content")}
      </a>

      <header className="z-40 shrink-0 border-b border-white/10 bg-flima-spruce text-white shadow-float">
        <div className="flex min-h-16 items-center justify-between gap-2 px-2 sm:gap-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen((previous) => !previous)}
              className="flex h-11 shrink-0 items-center gap-2 rounded-md border border-white/25 bg-white/10 px-3 text-white/90 transition-colors hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:hidden motion-reduce:transition-none"
              aria-label={t("sidebar_toggle")}
              aria-expanded={sidebarOpen}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                {sidebarOpen ? (
                  <path d="M6 6l12 12M18 6 6 18" />
                ) : (
                  // Funnel / filter icon
                  <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" />
                )}
              </svg>
              <span className="hidden text-sm font-semibold min-[380px]:inline">{t("filters")}</span>
            </button>
            <span
              className="hidden h-9 w-1 shrink-0 rounded-full bg-gradient-to-b from-white via-flima-river to-flima-lichen sm:block"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <h1 className="truncate font-display text-base font-semibold tracking-tight sm:text-lg">
                {config.branding.title}
              </h1>
              <p className="truncate font-utility text-[0.62rem] uppercase tracking-[0.14em] text-white/65">
                {config.region.name}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={toggleLang}
              className="min-h-11 rounded-md border border-white/20 bg-white/5 px-3 font-utility text-xs text-white/85 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-flima-spruce motion-reduce:transition-none"
              aria-label={t("language_toggle")}
            >
              {lang === "de" ? "EN" : "DE"}
            </button>
            <button
              type="button"
              onClick={handleTourStart}
              className="flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-flima-spruce motion-reduce:transition-none"
              aria-label={t("tour_restart")}
            >
              <span className="font-utility text-base" aria-hidden="true">?</span>
              <span className="hidden sm:inline">{t("tour_restart")}</span>
            </button>
            <MeasureCatalogueModal config={effectiveConfig} />
            <AboutPanel config={config} mode={mapMode} />
          </div>
        </div>
      </header>

      <MapModeSwitch
        mode={mapMode}
        onChange={handleMapModeChange}
        onMunicipalityClear={handleMunicipalityClear}
        onExport={handleOpenExport}
        selectedMunicipality={selectedMunicipalityName}
        warning={<WarningBanner text={t("warning")} contact={config.branding.contact} />}
      />

      {/*
        Z-index registry (keep new overlays consistent with this scale):
          root:  header z-40 (owns the About/Catalogue dropdowns and the
                 WarningBanner, which now lives inside the header), FloatingSelectionSummary (fixed) z-40
          <main> children: map toolbar z-10, LayerControls z-20,
                 MeasureDetails z-20, mobile scrim z-30, Sidebar (mobile) z-40,
                 map loading/error overlay z-30
        `<main>` is z-auto in the root context, so the header always sits above
        everything inside it — the hamburger stays reachable on mobile.
      */}
      <main id="main-content" className="relative flex min-h-0 flex-1 overflow-hidden">
        {/* Mobile scrim — tap to dismiss the slide-over sidebar. Only shown while
            open, and only below md (the sidebar is always docked at md+). */}
        {sidebarOpen ? (
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            aria-label={t("sidebar_toggle")}
            className="absolute inset-0 z-30 bg-flima-spruce/30 md:hidden"
          />
        ) : null}
        {/* The sidebar is always rendered so it can never become unreachable:
            at md+ it is permanently docked; below md `open` slides it in/out. */}
        <Sidebar
          config={effectiveConfig}
          filters={filters}
          mode={mapMode}
          mapZoom={mapZoom}
          open={sidebarOpen}
          highlightedMeasureIds={highlightedMeasureIds}
          lineOverlayMeasureId={lineOverlayMeasureId}
          presentMeasureIds={presentMeasureIds}
          hasNoWorkAreas={(noWorkData?.features.length ?? 0) > 0}
          hasSpeciesOverlap={
            mapMode === "habitats"
              ? (filteredSpeciesData?.features.some(
                  (f) => f.properties.species_id === "both",
                ) ?? false)
              : (filteredMeasuresData?.features.some(
                  (f) => f.properties.species_id === "both",
                ) ?? false)
          }
          highlightNoWork={highlightNoWork}
          lineOverlayNoWork={lineOverlayNoWork}
          onNoWorkHighlightToggle={handleNoWorkHighlightToggle}
          onNoWorkOverlayToggle={handleNoWorkOverlayToggle}
          onFilterChange={setFilters}
          onLegendMeasureClick={handleLegendMeasureClick}
          onLineOverlayToggle={handleLineOverlayToggle}
          onMeasureColorChange={handleMeasureColorChange}
          onResetMeasureColors={handleResetMeasureColors}
          onClose={() => setSidebarOpen(false)}
        />

        <div ref={mapExportRef} className="relative min-w-0 flex-1">
          {mapLoading ? (
            <div
              className="absolute inset-0 z-30 flex items-center justify-center bg-white/80 backdrop-blur-sm"
              role="status"
              aria-live="polite"
            >
              <div className="rounded-lg border border-flima-spruce/10 bg-white px-5 py-3 font-semibold text-flima-slate shadow-float">
                {t("loading")}
              </div>
            </div>
          ) : null}

          {mapError ? (
            <div
              className="absolute inset-0 z-30 flex items-center justify-center bg-white/90 p-6 backdrop-blur-sm"
              role="alert"
            >
              <p className="max-w-lg break-words rounded-lg border border-red-200 bg-white px-5 py-4 font-semibold text-red-700 shadow-float">
                {t("error")}: {mapError}
              </p>
            </div>
          ) : null}

          <WatercourseMap
            config={effectiveConfig}
            focusData={municipalityData}
            speciesData={filteredSpeciesData}
            measuresData={filteredMeasuresData}
            noWorkData={noWorkData}
            layersData={layersData}
            municipalityLocations={otherMunicipalityLocations}
            municipalityPreviewData={previewMunicipalityData}
            mode={mapMode}
            visibility={layerVisibility}
            selectedSpeciesCount={filters.species.size}
            basemap={basemap}
            onBasemapChange={setBasemap}
            highlightMeasureId={highlightMeasureId}
            lineOverlayMeasureId={lineOverlayMeasureId}
            highlightNoWork={highlightNoWork}
            lineOverlayNoWork={lineOverlayNoWork}
            selectionMode={selectionMode}
            polygonPoints={polygonPoints}
            polygonClosed={polygonClosed}
            viewportPolygon={viewportPolygon}
            onPolygonPointAdd={handlePolygonPointAdd}
            onPolygonFinish={handlePolygonFinish}
            onPolygonUndo={handlePolygonUndo}
            onSelectionClear={handleSelectionClear}
            onViewportCapture={handleViewportCapture}
            onZoomChange={handleZoomChange}
            onMeasureClick={handleMeasureClick}
            onMunicipalityPreview={handleMunicipalityPreview}
            onMunicipalitySelect={handleMapMunicipalitySelect}
          />
          <LayerControls
            config={config}
            mode={mapMode}
            visibility={layerVisibility}
            onToggle={handleLayerToggle}
          />
          {/* Legacy selection tools (polygon draw + viewport
              capture). Hidden via SHOW_LEGACY_SELECTION_TOOLS in favour of the
              export modal, but kept wired for a one-line revert. */}
          {SHOW_LEGACY_SELECTION_TOOLS && mapMode === "planning" ? (
            <div className="absolute top-3 left-16 z-20 flex max-w-[calc(100vw-4.5rem)] flex-wrap items-center gap-2 sm:left-16">
              <button
                  type="button"
                  onClick={handlePolygonToggle}
                  className={`flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg border px-3 text-xs font-medium shadow-float transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none ${
                    selectionMode === "polygon"
                      ? "border-flima-river bg-flima-river text-white"
                      : "border-flima-spruce/15 bg-white/90 text-flima-slate hover:border-flima-river hover:text-flima-spruce"
                  }`}
                  aria-pressed={selectionMode === "polygon"}
                  aria-label={t("polygon_selection_tool")}
                  title={t("polygon_selection_tool")}
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M3 6l6-3 9 4 3 8-5 6-8-2-5-7z" />
                  </svg>
                  <span className="hidden sm:inline">{t("polygon_selection_tool")}</span>
                </button>
                {selectionMode === "polygon" && !polygonClosed && polygonPoints.length >= 3 ? (
                  <button
                    type="button"
                    onClick={handlePolygonFinish}
                    className="flex min-h-11 items-center gap-1 rounded-lg border border-flima-river bg-flima-river px-3 text-xs font-medium text-white shadow-float focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2"
                  >
                    {t("finish_polygon")}
                  </button>
                ) : null}
                {selectionMode === "polygon" && !polygonClosed && polygonPoints.length > 0 ? (
                  <button
                    type="button"
                    onClick={handlePolygonUndo}
                    className="flex min-h-11 items-center gap-1 rounded-lg border border-flima-spruce/15 bg-white/90 px-3 text-xs font-medium text-flima-slate shadow-float transition-colors hover:border-flima-river focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none"
                  >
                    {t("undo_point")}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    const container = document.querySelector(".maplibregl-map") as HTMLElement & { __maplibreMap?: { getBounds: () => { getWest: () => number; getSouth: () => number; getEast: () => number; getNorth: () => number } } };
                    if (container?.__maplibreMap) {
                      const b = container.__maplibreMap.getBounds();
                      handleViewportCapture({
                        west: b.getWest(), south: b.getSouth(),
                        east: b.getEast(), north: b.getNorth(),
                      });
                    }
                  }}
                  className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg border border-flima-spruce/15 bg-white/90 px-3 text-xs font-medium text-flima-slate shadow-float transition-colors hover:border-flima-river hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none"
                  aria-label={t("capture_viewport")}
                  title={t("capture_viewport")}
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 3v18" />
                  </svg>
                  <span className="hidden sm:inline">{t("capture_viewport")}</span>
                </button>
            </div>
          ) : null}
          {/* Touch-friendly drawing hint while placing polygon points. */}
          {mapMode === "planning" && selectionMode === "polygon" && !polygonClosed ? (
            <div className="pointer-events-none absolute bottom-20 left-1/2 z-20 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-full bg-flima-spruce/95 px-4 py-2 text-center text-xs font-medium text-white shadow-float">
              {polygonPoints.length < 3 ? t("polygon_hint_add") : t("polygon_hint_finish")}
            </div>
          ) : null}
          {selectionMode !== "none" && activeSelectionRing ? (
            <FloatingSelectionSummary
              features={selectedAreaFeatures}
              config={effectiveConfig}
              selectionType={selectionMode === "viewport" ? "viewport" : "polygon"}
              selectedMunicipality={selectedMunicipalityName}
              onClose={handleSelectionClear}
              onExportPdf={handleExportPdf}
            />
          ) : null}
          <MeasureDetails
            measure={selectedMeasure}
            catalogue={catalogue}
            config={effectiveConfig}
            onClose={handleMeasureClose}
          />
        </div>
      </main>

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        config={effectiveConfig}
        initialMode={mapMode}
        municipality={selectedMunicipalityName}
        filters={filters}
        focusData={municipalityData}
        speciesData={filteredSpeciesData}
        measuresData={filteredMeasuresData}
        noWorkData={noWorkData}
        layersData={layersData}
        visibility={layerVisibility}
        initialCenter={exportView?.center ?? null}
        initialZoom={exportView?.zoom ?? null}
      />
      <OnboardingTour
        open={tourOpen}
        steps={tourSteps}
        stepIndex={tourStepIndex}
        firstStepIndex={APP_TOUR_START_STEP_INDEX}
        onStepChange={handleTourStepChange}
        onClose={handleTourDismiss}
        onComplete={handleTourComplete}
      />
    </div>
  );
}

export function App() {
  const { config, loading, error } = useConfig();

  if (loading) {
    return (
      <div className="flima-min-screen flex items-center justify-center bg-flima-spruce" role="status">
        {/* Shown before config (and the translation dictionary) has loaded, so
            this one string can't go through t(); the app is German-first. */}
        <span className="sr-only">Wird geladen…</span>
        <span
          className="h-2 w-24 rounded-full bg-gradient-to-r from-flima-river to-flima-lichen"
          aria-hidden="true"
        />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="flima-min-screen flex items-center justify-center bg-flima-mist p-6" role="alert">
        <p className="max-w-lg break-words rounded-lg border border-red-200 bg-white p-5 text-red-700 shadow-panel">
          {error || "Konfiguration nicht verfügbar"}
        </p>
      </div>
    );
  }

  return (
    <LanguageProvider uiText={config.ui_text}>
      <AppContent config={config} />
    </LanguageProvider>
  );
}
